import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useOperationStepValues from 'router/data/useOperationStepValues';
import utils from 'utils';
import { LOCATION_NAMES_MAPPING } from '../constants';
import AuthContext from 'providers/Auth/Auth.context';
import api from 'api';
import { NudgeNotification } from 'models/Notification';

export default function useBusinesses() {
  const [activeNudge, setActiveNudge] = useState<NudgeNotification>();

  const { isLoggedIn } = useContext(AuthContext);

  const { urlFilter } = useParams<{ urlFilter: string }>();

  const { industrySelectOptions, locationOptions } =
    useOperationStepValues(true);

  const { t } = useTranslation();

  const pageTitle = useMemo(() => {
    if (!urlFilter) return t('businessesForSale');

    const industryValue = industrySelectOptions.find(
      ({ value }) => utils.createSlug(value) === urlFilter,
    );

    const locationValue = locationOptions.find(
      ({ value }) => utils.createSlug(value) === urlFilter,
    );

    if (!industryValue && !locationValue) {
      return t('businessesForSale');
    }

    if (industryValue)
      return `${industryValue.value} ${t('businessesForSaleLabel')}`;

    if (locationValue)
      return `${t('businessesForSaleIn')} ${
        LOCATION_NAMES_MAPPING[
          locationValue.value.toLowerCase() as keyof typeof LOCATION_NAMES_MAPPING
        ]
      }`;
  }, [industrySelectOptions, locationOptions, t, urlFilter]);

  useEffect(() => {
    if (!isLoggedIn) return;

    (async () => {
      try {
        const {
          data: { notification },
        } = await api.nudges.getActiveNudgeNotification();

        if (notification) {
          setActiveNudge(notification);
        }
      } catch (e) {
        return;
      }
    })();
  }, [isLoggedIn]);

  return { pageTitle, activeNudge, setActiveNudge };
}
